/**
 * GraphsTrackPointsList Component
 * Displays track points in a tabular format with selection capability
 *
 * Requirements:
 * - 3.2: Render track points list in lower 50% of available vertical space
 * - 6.1: Show Source_Time and speed columns by default
 * - 4.1, 4.2: Support row selection with visual feedback and deselection
 */

import { useMemo, useCallback } from 'react';
import moment from 'moment';
import { DEFAULT_TRACK_POINTS_COLUMNS } from './constants';
import { getNestedValue } from './utils';

/**
 * Column labels mapping for display
 */
const COLUMN_LABELS = {
  source_time: 'Time',
  'velocity.speed': 'Speed',
  'voltage.external': 'External Battery Voltage',
  'voltage.internal': 'Internal Battery Voltage',
  mileage: 'Mileage (Distance Traveled)',
  fuel: 'Fuel',
  ignition: 'Ignition',
  'gps.position.lat': 'Latitude',
  'gps.position.lng': 'Longitude',
  'gps.position.alt': 'Altitude',
  server_time: 'Server Time',
  'gps.hdop': 'HDOP',
  'velocity.direction': 'Direction',
};

/**
 * Format value for display based on column type
 * @param {string} key - Column key
 * @param {*} value - Value to format
 * @returns {string} - Formatted value
 */
const formatValue = (key, value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // Format timestamp
  if (key === 'source_time' || key === 'server_time') {
    return moment(value).format('DD/MM/YY HH:mm:ss');
  }

  // Format speed (convert m/s to km/h)
  if (key === 'velocity.speed') {
    return `${(value * 3.6).toFixed(1)} km/h`;
  }

  // Format voltage
  if (key.includes('voltage')) {
    return `${Number(value).toFixed(2)} V`;
  }

  // Format mileage (convert to km if in meters)
  if (key === 'mileage') {
    return `${(value / 1000).toFixed(1)} km`;
  }

  // Format fuel
  if (key === 'fuel') {
    return `${Number(value).toFixed(0)}%`;
  }

  // Format altitude
  if (key === 'gps.position.alt') {
    return `${Number(value).toFixed(1)} m`;
  }

  // Format direction (degrees)
  if (key === 'velocity.direction') {
    return `${Number(value).toFixed(0)}°`;
  }

  // Format boolean
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Format number
  if (typeof value === 'number') {
    return value.toFixed(2);
  }

  return String(value);
};

/**
 * Get column label for display
 * @param {string} key - Column key
 * @returns {string} - Display label
 */
const getColumnLabel = (key) => {
  return COLUMN_LABELS[key] || key.split('.').pop();
};

/**
 * Get unique identifier for a track point
 * @param {Object} point - Track point object
 * @param {number} index - Fallback index
 * @returns {string|number} - Unique identifier
 */
const getPointId = (point, index) => {
  return point?.packet_id || point?.['packet_id'] || `point-${index}`;
};

/**
 * Check if two track points are the same
 * @param {Object} point1 - First track point
 * @param {Object} point2 - Second track point
 * @returns {boolean} - True if points are the same
 */
const isSamePoint = (point1, point2) => {
  if (!point1 || !point2) return false;

  const id1 = point1.packet_id || point1['packet_id'];
  const id2 = point2.packet_id || point2['packet_id'];

  // Match by packet_id if available
  if (id1 && id2) {
    return id1 === id2;
  }

  // Fall back to matching by source_time
  const time1 = point1.source_time || point1['source_time'];
  const time2 = point2.source_time || point2['source_time'];
  return time1 === time2;
};

/**
 * GraphsTrackPointsList Component
 *
 * @param {Object} props
 * @param {Array} props.data - Array of track point objects
 * @param {Object} props.selectedPoint - Currently selected track point
 * @param {Function} props.onRowSelect - Callback when a row is selected/deselected
 * @param {Array} props.selectedColumns - Array of column keys to display (defaults to source_time and speed)
 */
export const GraphsTrackPointsList = ({
  data = [],
  selectedPoint = null,
  onRowSelect,
  selectedColumns = DEFAULT_TRACK_POINTS_COLUMNS,
}) => {
  /**
   * Handle row click - supports selection and deselection on second click
   * Requirements 4.1, 4.2: Highlight selected row, support deselection
   */
  const handleRowClick = useCallback((point) => {
    if (!onRowSelect) return;

    // If clicking the same point that's already selected, deselect it (pass null)
    if (isSamePoint(point, selectedPoint)) {
      onRowSelect(null);
    } else {
      // Otherwise, select the new point
      onRowSelect(point);
    }
  }, [onRowSelect, selectedPoint]);

  /**
   * Check if a row is currently selected
   */
  const isRowSelected = useCallback((point) => {
    return isSamePoint(point, selectedPoint);
  }, [selectedPoint]);

  /**
   * Sort data by source_time descending (most recent first)
   */
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...data].sort((a, b) => {
      const timeA = a.source_time || a['source_time'] || 0;
      const timeB = b.source_time || b['source_time'] || 0;
      return timeB - timeA;
    });
  }, [data]);

  /**
   * Ensure source_time is always first in the columns list
   * Requirements 6.1: Show Source_Time and speed columns by default
   */
  const orderedColumns = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return DEFAULT_TRACK_POINTS_COLUMNS;
    }

    // Ensure source_time comes first if present
    const columns = [...selectedColumns];
    const sourceTimeIndex = columns.indexOf('source_time');

    if (sourceTimeIndex > 0) {
      columns.splice(sourceTimeIndex, 1);
      columns.unshift('source_time');
    } else if (sourceTimeIndex === -1) {
      // Add source_time at the beginning if not present
      columns.unshift('source_time');
    }

    return columns;
  }, [selectedColumns]);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="graphs-track-points-list graphs-track-points-list--empty">
        <p>No track points available</p>
      </div>
    );
  }

  return (
    <div className="graphs-track-points-list" data-testid="graphs-track-points-list">
      <table className="graphs-track-points-table">
        <thead>
          <tr>
            {orderedColumns.map((column) => (
              <th key={column} data-testid={`column-header-${column}`}>
                {getColumnLabel(column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((point, index) => {
            const pointId = getPointId(point, index);
            const selected = isRowSelected(point);

            return (
              <tr
                key={pointId}
                data-testid={`track-point-row-${index}`}
                className={`graphs-track-points-row ${selected ? 'graphs-track-points-row--selected' : ''}`}
                onClick={() => handleRowClick(point)}
                role="button"
                tabIndex={0}
                aria-selected={selected}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(point);
                  }
                }}
              >
                {orderedColumns.map((column) => {
                  const value = getNestedValue(point, column);
                  return (
                    <td key={column} data-testid={`cell-${column}-${index}`}>
                      {formatValue(column, value)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default GraphsTrackPointsList;
